import { Avatar, Divider, Typography } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./ConnectPractitionerCard.styles";

interface Props {
  pracName: string;
  pracAvatar?: string;
  pracFirstName?: string;
  pracLastName?: string;
  practitionerType?: string;
  location?: string;
  firstVisitPrice?: string;
  subsequentVisitPrice?: string;
}

const ConnectPractitionerCard = ({
  pracName,
  pracAvatar,
  pracFirstName,
  pracLastName,
  practitionerType,
  location,
  firstVisitPrice,
  subsequentVisitPrice,
}: Props) => {
  const { t } = useTranslation();

  const hasPricing = !!firstVisitPrice || !!subsequentVisitPrice;

  const renderAvatar = () => {
    const alt = t(l.connect.pracAvatarAlt, { pracName });

    if (pracAvatar) return <Avatar imageUrl={pracAvatar} size="medium" alt={alt} />;

    if (!pracFirstName || !pracLastName) return null;

    return <Avatar firstName={pracFirstName} lastName={pracLastName} size="medium" alt={alt} />;
  };

  const renderTypeAndLocation = () => {
    if (!practitionerType || !location) return null;

    return (
      <Typography variant="footnote" colorIntensity="subtle" isMarginless>
        {t(l.connect.practitionerCard.typeAndLocation, { practitionerType, location })}
      </Typography>
    );
  };

  const renderPricing = () => {
    if (!hasPricing) return null;

    return (
      <>
        <Divider customCss={styles.divider} />
        <div css={styles.pricing}>
          {!!firstVisitPrice && (
            <div css={styles.pricingRow}>
              <Typography variant="footnote" isMarginless>
                {t(l.connect.practitionerCard.firstVisit)}
              </Typography>
              <Typography
                variant="footnote"
                fontWeight="bold"
                isMarginless
                customCss={styles.firstVisitPrice}
              >
                {firstVisitPrice}
              </Typography>
            </div>
          )}
          {!!subsequentVisitPrice && (
            <div css={styles.pricingRow}>
              <Typography variant="footnote" isMarginless>
                {t(l.connect.practitionerCard.subsequentVisit)}
              </Typography>
              <Typography variant="footnote" fontWeight="bold" colorIntensity="bold" isMarginless>
                {subsequentVisitPrice}
              </Typography>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div css={styles.card}>
      <div css={styles.identity}>
        {renderAvatar()}
        <div>
          <Typography variant="footnote" colorIntensity="subtle" isMarginless>
            {t(l.connect.practitionerCard.requestingAppointmentWith)}
          </Typography>
          <Typography as="h2" variant="h3" colorIntensity="bold" isMarginless>
            {pracName}
          </Typography>
          {renderTypeAndLocation()}
        </div>
      </div>
      {renderPricing()}
    </div>
  );
};

export { ConnectPractitionerCard };
export type { Props as ConnectPractitionerCardProps };
